/**
 * The run-mode pane for a Document Repository (obj_type "K").
 *
 * Thin by design: it resolves the transport and mounts the SHARED
 * `RepositoryView` from `@twasta/page-runtime/dms` — the same component the
 * deployed standalone runtime mounts. The platform Run view and a downloaded
 * app therefore cannot drift in what they show or in which controls a given
 * set of rights produces.
 *
 * The AUTHORING sub-module is mounted the same way, but LAZILY. The editor
 * carries an editing engine and a PDF renderer, and most people opening a
 * repository are reading a document rather than writing one — loading that on
 * every visit would make the common case pay for the rare one.
 */

import { Suspense, lazy, useEffect, useMemo, useState } from 'react'
import { RepositoryView } from '@twasta/page-runtime/dms'
import type { AuthoringMode } from '@twasta/page-runtime/dms/authoring'
import { loadRepository, makeAuthoringClient, makeDmsClient } from '@/api/dms'
import '@twasta/page-runtime/dms/addDocuments.css'
import '@twasta/page-runtime/dms/authoring/editor.css'

const AuthoringHost = lazy(async () => ({
  default: (await import('@twasta/page-runtime/dms/authoring')).AuthoringHost,
}))

export function DocumentRepositoryPane({
  project, objName, initialDocumentId, onDocumentChange, onOpenSource,
}: {
  project: string
  objName: string
  /** Deep-link straight to one document (`?doc=<id>` in the Run view). */
  initialDocumentId?: string
  onDocumentChange?: (documentId: string | null) => void
  /**
   * Open the RECORD a Document Form filed a document from. Supplied by the Run
   * shell, which is the only surface here that can move between objects — the
   * Prototype tab's preview leaves it out and gets the ordinary editor.
   */
  onOpenSource?: (source: { obj_name: string; txn_id: string }) => void
}) {
  const client = useMemo(() => makeDmsClient(project, objName), [project, objName])
  const authoring = useMemo(() => makeAuthoringClient(project, objName),
    [project, objName])
  const [title, setTitle] = useState(objName)
  const [qaEnabled, setQaEnabled] = useState(false)
  const [error, setError] = useState('')
  const [mode, setMode] = useState<AuthoringMode | null>(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    let live = true
    void (async () => {
      try {
        const res = await loadRepository(project, objName)
        if (!live) return
        const repository = (res.content as { repository?: Record<string, unknown> })
          ?.repository
        if (repository) {
          setTitle(String(repository.title || objName))
          setQaEnabled(Boolean((repository.ai as { qa_enabled?: boolean })?.qa_enabled))
        }
      } catch (loadError) {
        if (live) {
          setError(loadError instanceof Error ? loadError.message
            : 'This repository could not be opened.')
        }
      }
    })()
    return () => { live = false }
  }, [project, objName])

  if (error) {
    return (
      <div style={{ padding: 24, fontSize: 16, lineHeight: 1.55, color: 'var(--color-error)' }}>
        {error}
      </div>
    )
  }

  return (
    <>
      <RepositoryView
        key={reloadKey}
        client={client}
        title={title}
        objName={objName}
        qaEnabled={qaEnabled}
        onAuthor={setMode}
        onOpenSource={onOpenSource}
        initialDocumentId={initialDocumentId}
        onDocumentChange={onDocumentChange}
      />
      {mode ? (
        <Suspense fallback={
          <div style={{ position: 'fixed', inset: 0, display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        background: 'var(--color-input-bg)', zIndex: 40,
                        fontSize: 15 }}>
            Loading the document editor…
          </div>
        }>
          <AuthoringHost
            client={authoring}
            mode={mode}
            onClose={() => { setMode(null); setReloadKey((k) => k + 1) }}
            // A filed version changes the list behind the editor, and the
            // reader must not come back to a document list that still shows the
            // state before they wrote.
            onSaved={() => setReloadKey((k) => k + 1)}
          />
        </Suspense>
      ) : null}
    </>
  )
}

export default DocumentRepositoryPane
